/**
 * Compressed IMAP UID sets (`"1:5,7,9:12"`) as returned by `/api/messages?reconcile=1` and
 * `/api/sync` (`uidSet`). The client derives vanished rows by testing each cached uid.
 */

export type UidRange = [number, number];

/** Parse into sorted, non-overlapping ranges. Malformed pieces are ignored. */
export function parseUidRanges(set: string | null | undefined): UidRange[] {
  const out: UidRange[] = [];
  if (!set) return out;
  for (const piece of set.split(',')) {
    const [a, b] = piece.split(':');
    const lo = Number(a);
    const hi = b === undefined ? lo : Number(b);
    if (Number.isFinite(lo) && Number.isFinite(hi) && a !== '' && a !== undefined) {
      out.push([Math.min(lo, hi), Math.max(lo, hi)]);
    }
  }
  out.sort((x, y) => x[0] - y[0]);
  // merge touching/overlapping ranges so binary search stays correct
  const merged: UidRange[] = [];
  for (const r of out) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1] + 1) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  return merged;
}

/** Binary search a uid in sorted ranges. */
export function rangesInclude(ranges: UidRange[], uid: number): boolean {
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const [a, b] = ranges[mid]!;
    if (uid < a) hi = mid - 1;
    else if (uid > b) lo = mid + 1;
    else return true;
  }
  return false;
}

/** Membership checker over a compressed set: `has(uid)` plus the parsed ranges. */
export interface UidSet {
  has(uid: number): boolean;
  ranges: UidRange[];
  /** Expand to a concrete `Set<number>` — fine for typical mailboxes, avoid for huge ones. */
  toSet(): Set<number>;
  size(): number;
}

export function parseUidSet(set: string | null | undefined): UidSet {
  const ranges = parseUidRanges(set);
  return {
    ranges,
    has: (uid) => rangesInclude(ranges, uid),
    toSet() {
      const s = new Set<number>();
      for (const [a, b] of ranges) for (let u = a; u <= b; u++) s.add(u);
      return s;
    },
    size() {
      let n = 0;
      for (const [a, b] of ranges) n += b - a + 1;
      return n;
    },
  };
}

/** Compress a list of uids back into the IMAP range syntax (used by tests and debugging). */
export function formatUidSet(uids: Iterable<number>): string {
  const sorted = [...new Set(uids)].sort((a, b) => a - b);
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i]!;
    let end = start;
    while (i + 1 < sorted.length && sorted[i + 1] === end + 1) {
      i++;
      end = sorted[i]!;
    }
    parts.push(start === end ? String(start) : `${start}:${end}`);
    i++;
  }
  return parts.join(',');
}
